<script setup>
import { ref, computed } from 'vue';

const props = defineProps({
    hired: {
        type: Array,
        required: true,
    },
    slotLimit: {
        type: Number,
        required: true,
    },
    specialists: {
        type: Array,
        required: true,
    },
});

const emit = defineEmits(['close', 'updated']);

const localHired = ref([...props.hired]);
const localSlotLimit = ref(props.slotLimit);
const actionLoading = ref(false);
const dragOverSlot = ref(null);

const usedSlots = computed(() => localHired.value.length);
const remainingSlots = computed(() => Math.max(0, localSlotLimit.value - usedSlots.value));
const allSlotsFilled = computed(() => remainingSlots.value === 0);

const filledSlots = computed(() => {
    return localHired.value.map(key => props.specialists.find(s => s.key === key)).filter(Boolean);
});

const availableSpecialists = computed(() => {
    return props.specialists.filter(s => !localHired.value.includes(s.key));
});

function onDragStart(event, specialistKey) {
    event.dataTransfer.setData('text/plain', specialistKey);
    event.dataTransfer.effectAllowed = 'move';
}

function onDragOver(event, slotIndex) {
    event.preventDefault();
    dragOverSlot.value = slotIndex;
}

function onDragLeave() {
    dragOverSlot.value = null;
}

function onDrop(event, slotIndex) {
    event.preventDefault();
    dragOverSlot.value = null;
    const specialistKey = event.dataTransfer.getData('text/plain');
    if (specialistKey) {
        hireSpecialist(specialistKey);
    }
}

async function hireSpecialist(specialistKey) {
    if (actionLoading.value || allSlotsFilled.value) return;
    if (localHired.value.includes(specialistKey)) return;

    actionLoading.value = true;
    try {
        const response = await axios.post('/bo/ai-team/hire', { specialist: specialistKey });
        localHired.value = response.data.hired;
        localSlotLimit.value = response.data.slot_limit;
        emit('updated', response.data);
    } catch (e) {
        // Silently fail
    } finally {
        actionLoading.value = false;
    }
}

async function fireSpecialist(specialistKey) {
    if (actionLoading.value) return;

    actionLoading.value = true;
    try {
        const response = await axios.post('/bo/ai-team/fire', { specialist: specialistKey });
        localHired.value = response.data.hired;
        localSlotLimit.value = response.data.slot_limit;
        emit('updated', response.data);
    } catch (e) {
        // Silently fail
    } finally {
        actionLoading.value = false;
    }
}

function closeModal() {
    emit('close');
}
</script>

<template>
    <div class="fixed inset-0 z-modal-backdrop flex items-center justify-center bg-black/50 !mt-0" @click.self="closeModal">
        <div class="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-white rounded-xl shadow-2xl mx-4">
            <!-- Header -->
            <div class="sticky top-0 z-10 flex items-center justify-between p-6 bg-white border-b rounded-t-xl">
                <div>
                    <h2 class="text-lg font-bold text-gray-800">{{ _mft('dashboard:aiSpecialists.hiringModal.title') }}</h2>
                    <p class="text-sm text-gray-500">
                        {{ usedSlots }} / {{ localSlotLimit }} {{ _mft('dashboard:aiSpecialists.hiringModal.slotsUsed') }}
                    </p>
                </div>
                <button-base
                    size="xs"
                    style-type="default"
                    class="!border-0 !bg-transparent !px-2 !py-2 !text-gray-400"
                    @click="closeModal"
                >
                    <template #icon>
                        <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </template>
                </button-base>
            </div>

            <div class="p-6 space-y-6">
                <!-- Slot grid -->
                <div class="grid grid-cols-2 gap-3 sm:grid-cols-4">
                    <!-- Filled slots -->
                    <div
                        v-for="specialist in filledSlots"
                        :key="'slot-' + specialist.key"
                        class="relative p-4 rounded-lg group flex flex-col justify-center items-center"
                        :class="specialist.bgClass"
                    >
                        <div class="flex flex-col items-center text-center cursor-default select-none">
                            <div
                                class="flex items-center justify-center w-10 h-10 mb-2 bg-white/50 rounded-full"
                            >
                                <!-- Stella: Web Designer -->
                                <div v-if="specialist.key === 'stella'">
                                    <ai-agent-web-designer-avatar class="w-full h-full" />
                                </div>
                                <!-- Susan: Data Analyst -->
                                <div v-else-if="specialist.key === 'susan'">
                                    <ai-agent-data-analyst-avatar class="w-full h-full" />
                                </div>
                                <!-- Tom: Relationship Manager -->
                                <div v-else-if="specialist.key === 'tom'">
                                    <ai-agent-relationship-manager-avatar class="w-full h-full" />
                                </div>
                                <!-- Maya: Content Creator -->
                                <div v-else-if="specialist.key === 'maya'">
                                    <ai-agent-content-creator-avatar class="w-full h-full" />
                                </div>
                                <!-- Alex: Newsletter Specialist -->
                                <div v-else-if="specialist.key === 'alex'">
                                    <ai-agent-newsletter-specialist-avatar class="w-full h-full" />
                                </div>
                                <!-- Nina: Sales Assistant -->
                                <div v-else-if="specialist.key === 'nina'">
                                    <ai-agent-sales-assistant-avatar class="w-full h-full" />
                                </div>
                                <!-- Oscar: Lead Finder -->
                                <div v-else-if="specialist.key === 'oscar'">
                                    <ai-agent-lead-finder-avatar class="w-full h-full" />
                                </div>
                                <!-- Leo: Automation Expert -->
                                <div v-else-if="specialist.key === 'leo'">
                                    <ai-agent-automation-expert-avatar class="w-full h-full" />
                                </div>
                            </div>
                            <span class="text-xs font-semibold text-gray-700">
                                {{ _mft('dashboard:aiSpecialists.' + specialist.key + '.role') }}
                            </span>
                            <span class="text-xs text-gray-500">
                                {{ _mft('dashboard:aiSpecialists.' + specialist.key + '.name') }}
                            </span>
                        </div>
                        <div class="absolute top-0 right-0">
                            <button-base
                                size="xs"
                                style-type="default"
                                class="absolute opacity-0 top-1.5 right-1.5 group-hover:opacity-100 !rounded-full !px-1 !py-1 !text-red-500"
                                :title="_mft('dashboard:aiSpecialists.hiringModal.fire')"
                                @click="fireSpecialist(specialist.key)"
                            >
                                <template #icon>
                                    <svg class="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                        <line x1="18" y1="6" x2="6" y2="18" />
                                        <line x1="6" y1="6" x2="18" y2="18" />
                                    </svg>
                                </template>
                            </button-base>
                        </div>
                    </div>

                    <!-- Empty slots -->
                    <div
                        v-for="n in remainingSlots"
                        :key="'empty-slot-' + n"
                        class="flex items-center justify-center p-4 transition-all border-2 border-gray-200 border-dashed rounded-lg aspect-square"
                        :class="{ 'border-indigo-400 bg-indigo-50 scale-105': dragOverSlot === n }"
                        @dragover="onDragOver($event, n)"
                        @dragleave="onDragLeave"
                        @drop="onDrop($event, n)"
                    >
                        <svg class="w-5 h-5 text-gray-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <line x1="12" y1="5" x2="12" y2="19" />
                            <line x1="5" y1="12" x2="19" y2="12" />
                        </svg>
                    </div>
                </div>

                <!-- Upgrade message when all slots filled -->
                <p v-if="allSlotsFilled && localSlotLimit < 8" class="text-sm text-center text-amber-600">
                    {{ _mft('dashboard:aiSpecialists.hiringModal.upgradeForMore') }}
                </p>

                <!-- Available specialists -->
                <div>
                    <h3 v-if="! allSlotsFilled" class="mb-3 text-sm font-semibold text-gray-600">{{ _mft('dashboard:aiSpecialists.hiringModal.available') }}</h3>
                    <p v-if="!allSlotsFilled" class="mb-3 text-xs text-gray-400">{{ _mft('dashboard:aiSpecialists.hiringModal.dragToHire') }}</p>
                    <div class="grid grid-cols-2 gap-3 sm:grid-cols-4">
                        <div
                            v-for="specialist in availableSpecialists"
                            :key="'available-' + specialist.key"
                            class="relative p-4 transition-all rounded-lg cursor-grab aspect-square flex items-center justify-center"
                            :class="allSlotsFilled ? 'bg-gray-50 opacity-50 cursor-not-allowed' : 'bg-gray-50 hover:bg-gray-100 hover:shadow-sm'"
                            :draggable="!allSlotsFilled"
                            @dragstart="onDragStart($event, specialist.key)"
                            @click="!allSlotsFilled && hireSpecialist(specialist.key)"
                        >
                            <div class="flex flex-col items-center text-center">
                                <div
                                    class="flex items-center justify-center w-10 h-10 mb-2 rounded-full"
                                    :class="specialist.bgClass"
                                >
                                    <!-- Stella: Web Designer -->
                                    <div v-if="specialist.key === 'stella'">
                                        <ai-agent-web-designer-avatar class="w-full h-full" />
                                    </div>
                                    <!-- Susan: Data Analyst -->
                                    <div v-else-if="specialist.key === 'susan'">
                                        <ai-agent-data-analyst-avatar class="w-full h-full" />
                                    </div>
                                    <!-- Tom: Relationship Manager -->
                                    <div v-else-if="specialist.key === 'tom'">
                                        <ai-agent-relationship-manager-avatar class="w-full h-full" />
                                    </div>
                                    <!-- Maya: Content Creator -->
                                    <div v-else-if="specialist.key === 'maya'">
                                        <ai-agent-content-creator-avatar class="w-full h-full" />
                                    </div>
                                    <!-- Alex: Newsletter Specialist -->
                                    <div v-else-if="specialist.key === 'alex'">
                                        <ai-agent-newsletter-specialist-avatar class="w-full h-full" />
                                    </div>
                                    <!-- Nina: Sales Assistant -->
                                    <div v-else-if="specialist.key === 'nina'">
                                        <ai-agent-sales-assistant-avatar class="w-full h-full" />
                                    </div>
                                    <!-- Oscar: Lead Finder -->
                                    <div v-else-if="specialist.key === 'oscar'">
                                        <ai-agent-lead-finder-avatar class="w-full h-full" />
                                    </div>
                                    <!-- Leo: Automation Expert -->
                                    <div v-else-if="specialist.key === 'leo'">
                                        <ai-agent-automation-expert-avatar class="w-full h-full" />
                                    </div>
                                </div>
                                <span class="text-xs font-semibold text-gray-700">
                                    {{ _mft('dashboard:aiSpecialists.' + specialist.key + '.role') }}
                                </span>
                                <span class="text-xs text-gray-500">
                                    {{ _mft('dashboard:aiSpecialists.' + specialist.key + '.name') }}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
